import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Dispatch, SetStateAction } from 'react';
import type { GroupType, Selection } from './types';

export type TreeState = Record<Selection, number | null> & {
    type: Selection | null;
    groups: GroupType[];
};

// Allow the parent to manage the state of this component
// ? Necessary, as the selection state is needed in the parent
export const useTreeState = () => {
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams());

    const state = useMemo(() => {
        const { company, reseller, location, resellerLocation, type, groups } = Object.fromEntries(searchParams);

        return {
            company: company ? +company : null,
            reseller: reseller ? +reseller : null,
            location: location ? +location : null,
            resellerLocation: resellerLocation ? +resellerLocation : null,
            type: type || null,
            groups: groups?.split(',') ?? [],
        } as TreeState;
    }, [searchParams]);

    const set: Dispatch<SetStateAction<TreeState>> = (action) => {
        const next = typeof action === 'function' ? action(state) : action;

        setSearchParams(sanitizeParams(next));
    };

    return [state, set] as const;
};

function sanitizeParams(data: Record<string, string | number | null | string[]>) {
    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
        if (value === null) return;
        const val = Array.isArray(value) ? value.join(',') : value.toString();
        if (val) params.set(key, val);
    });

    return params;
}
